// ./src/hooks/useEventUpgrade.ts
//
// ROADMAP #209 — upgrading ONE event to a plan, without leaving the page (partylabz sells the event, not a subscription).
// What the event uses and its plan does not include comes from the server's own publish check (`eventPlanCheck`); the
// upgrades it can be bought up to from ms_auth's catalog; the payment is the platform rail's PaymentElement, as a top-up.
//
// ⚠ PAYING IS NOT THE UPGRADE. Stripe taking the money is followed by the grant: this CONFIRMS the payment at once
//   (`AppAddonService.confirmPayment` — ms_auth reads it from Stripe and grants, as its webhook would), and eventsmagic
//   forgets the event's cached plan when it hears of it. Then this POLLS the event's access until the bought plan is there
//   (`myEventAccess.eventUpgrade`) — and a timeout is NOT a failure: the host has been charged, only the grant is late
//   ("slow", with a Check again, which confirms again).
import { useCallback, useEffect, useRef, useState } from "react";
import { OpResult } from "@sdflc/api-helpers";

import { AppAddonService, CrewAccessService, EventPlanCheck, EventService, EventUpgradeOption, EventUpgradeService } from "@/utils/api";
import { orderUpgradeOptions } from "@/utils/eventUpgrade";
import { PURCHASE_KIND, trackPurchase } from "@/utils/analytics";

const POLL_MS = 1500;
const POLL_ATTEMPTS = 40;

export type EventUpgradePhase = "idle" | "creating" | "paying" | "confirming" | "arrived" | "slow";

export interface UseEventUpgradeReturn {
  /** What the event uses and what its plan says — null until read. */
  check: EventPlanCheck | null;
  /** The upgrades, covering ones first (`orderUpgradeOptions`). */
  options: EventUpgradeOption[];
  loading: boolean;
  /** A READ that failed (the check or the catalog) — shown at the top. */
  lastError: OpResult | null;
  /** A purchase the server refused (an upgrade it will not sell) — shown beside the button that asked (`chosen`). */
  buyError: OpResult | null;
  phase: EventUpgradePhase;
  /** The upgrade being bought. */
  chosen: EventUpgradeOption | null;
  /** For the PaymentElement (platform key — no connected account). */
  clientSecret: string | null;
  publishableKey: string | null;
  /** Read the check and the catalog again (on open; after a purchase). */
  load: () => Promise<void>;
  /** Start paying for an upgrade. */
  buy: (option: EventUpgradeOption) => Promise<void>;
  /** Stripe took the money: wait for the grant. */
  paid: () => void;
  /** From the card form back to the choice. */
  back: () => void;
  /** After a slow grant: look again. */
  checkAgain: () => void;
}

export const useEventUpgrade = (eventId: string | null | undefined, opened: boolean): UseEventUpgradeReturn => {
  const [check, setCheck] = useState<EventPlanCheck | null>(null);
  const [options, setOptions] = useState<EventUpgradeOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [lastError, setLastError] = useState<OpResult | null>(null);
  const [buyError, setBuyError] = useState<OpResult | null>(null);
  const [phase, setPhase] = useState<EventUpgradePhase>("idle");
  const [chosen, setChosen] = useState<EventUpgradeOption | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [publishableKey, setPublishableKey] = useState<string | null>(null);
  // A newer read, or a closed drawer, makes an older answer stale.
  const callRef = useRef(0);
  const pollRef = useRef(0);
  // The started payment (its id, the amount charged, the currency, a code) — for the analytics `purchase` once paid.
  const intentRef = useRef<{ paymentIntentId?: string | null; amount?: number | null; currency?: string | null; discountCode?: string | null } | null>(null);

  const load = useCallback(async () => {
    if (!eventId) {
      return;
    }

    const myCall = ++callRef.current;

    setLoading(true);
    setLastError(null);

    const [checked, catalog] = await Promise.all([EventService.planCheck(eventId), EventUpgradeService.catalog(eventId)]);

    if (myCall !== callRef.current) {
      return;
    }

    setLoading(false);

    if (!checked.didSucceed()) {
      setLastError(checked);

      return;
    }

    const nextCheck = (checked.getDataFirst() as EventPlanCheck | undefined) ?? null;

    setCheck(nextCheck);

    // ⚠ A catalog that could not be read leaves the CHECK on screen (what publishing needs is still worth knowing).
    if (!catalog.didSucceed()) {
      setLastError(catalog);
      setOptions([]);

      return;
    }

    const rows = Array.isArray(catalog.data) ? (catalog.data as EventUpgradeOption[]) : [];

    setOptions(orderUpgradeOptions(rows, nextCheck?.uses ?? []));
  }, [eventId]);

  useEffect(() => {
    if (!opened) {
      // A closed drawer ends whatever it was doing; an answer arriving later lands on nothing.
      callRef.current += 1;
      pollRef.current += 1;
      setPhase("idle");
      setChosen(null);
      setClientSecret(null);
      setLastError(null);
      setBuyError(null);

      return;
    }

    void load();
  }, [opened, load]);

  /**
   * Confirm the payment (when there is one), then wait for the bought plan to show on the event's access (the grant), then
   * re-read the check. ⓘ The confirm's answer is not awaited for its verdict: a refused or failed confirm still leaves the
   * webhook to grant, and the poll is what says the plan is there.
   */
  const waitForGrant = useCallback(
    async (planCode: string | null | undefined, paymentIntentId?: string | null) => {
      if (!eventId) {
        return;
      }

      const myPoll = ++pollRef.current;

      setPhase("confirming");

      if (paymentIntentId) {
        await AppAddonService.confirmPayment(paymentIntentId).catch(() => null);

        if (myPoll !== pollRef.current) {
          return;
        }
      }

      for (let attempt = 0; attempt < POLL_ATTEMPTS; attempt += 1) {
        const res = await CrewAccessService.myEventAccess(eventId).catch(() => null);

        if (myPoll !== pollRef.current) {
          return;
        }

        const upgrade = res?.didSucceed() ? (res.getDataFirst() as any)?.eventUpgrade : null;

        if (upgrade && (!planCode || upgrade.planCode === planCode)) {
          setPhase("arrived");
          await load();

          return;
        }

        await new Promise((resolve) => setTimeout(resolve, POLL_MS));

        if (myPoll !== pollRef.current) {
          return;
        }
      }

      setPhase("slow");
    },
    [eventId, load],
  );

  /** The analytics `purchase` of this event's upgrade, at what was charged (`utils/analytics/purchase.ts`). */
  const reportPurchase = useCallback(
    (option: EventUpgradeOption, intent: { paymentIntentId?: string | null; amount?: number | null; currency?: string | null; discountCode?: string | null } | null) => {
      trackPurchase({
        transactionId: intent?.paymentIntentId,
        kind: PURCHASE_KIND.EVENT_UPGRADE,
        itemId: option.providerSku,
        itemName: option.planName || option.name,
        amount: intent?.amount,
        currency: intent?.currency,
        coupon: intent?.discountCode,
      });
    },
    [],
  );

  const buy = useCallback(
    async (option: EventUpgradeOption) => {
      if (!eventId || !option?.providerSku) {
        return;
      }

      setChosen(option);
      setBuyError(null);
      setPhase("creating");

      const res = await EventUpgradeService.createPaymentIntent(eventId, option.providerSku);

      if (!res.didSucceed()) {
        setBuyError(res);
        setPhase("idle");

        return;
      }

      const intent = res.getDataFirst() as
        | {
            clientSecret?: string | null;
            publishableKey?: string | null;
            granted?: boolean | null;
            paymentIntentId?: string | null;
            amount?: number | null;
            currency?: string | null;
            discountCode?: string | null;
          }
        | undefined;

      intentRef.current = intent ?? null;

      // ⚠ Granted outright (a code covered the whole price): nothing to pay — wait for the plan. Still a purchase (value 0).
      if (intent?.granted) {
        reportPurchase(option, { ...intent, paymentIntentId: intent.paymentIntentId || `granted:${option.providerSku}:${eventId}`, amount: 0 });
        void waitForGrant(option.planCode);

        return;
      }

      if (!intent?.clientSecret || !intent?.publishableKey) {
        setBuyError(new OpResult({ code: -1 }).addError("", "The payment could not be started. Try again in a moment."));
        setPhase("idle");

        return;
      }

      setClientSecret(intent.clientSecret);
      setPublishableKey(intent.publishableKey);
      setPhase("paying");
    },
    [eventId, waitForGrant, reportPurchase],
  );

  const paid = useCallback(() => {
    // Stripe took the card: the upgrade is bought (the plan arriving on the event is the grant, not the sale).
    if (chosen) {
      reportPurchase(chosen, intentRef.current);
    }

    void waitForGrant(chosen?.planCode, intentRef.current?.paymentIntentId);
  }, [waitForGrant, chosen, reportPurchase]);

  const back = useCallback(() => {
    setPhase("idle");
    setClientSecret(null);
  }, []);

  const checkAgain = useCallback(() => {
    // Confirms again too: a confirm that failed the first time (offline) is what makes this grant.
    void waitForGrant(chosen?.planCode, intentRef.current?.paymentIntentId);
  }, [waitForGrant, chosen?.planCode]);

  return { check, options, loading, lastError, buyError, phase, chosen, clientSecret, publishableKey, load, buy, paid, back, checkAgain };
};
